import { Table, Tag, type TableColumnsType } from 'antd';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { describeCargo, laneOf } from '../../../policies/rate-line-key';
import type { RateLineImportRowView } from '../../../rate-cards.api';
import { ROW_STATUS_COLORS, ROW_STATUS_LABELS } from '../policies/rate-line-import-row-status';

function KeyCell({ row }: { row: RateLineImportRowView }) {
  const { originZoneCode, destinationZoneCode, jobTypeCode } = row;
  if (!originZoneCode || !destinationZoneCode || !jobTypeCode) return <span className="text-muted">—</span>;
  const cargo = describeCargo({
    containerSizeCode: row.containerSizeCode ?? null,
    containerTypeCode: row.containerTypeCode ?? null,
    weightBandCode: row.weightBandCode ?? null,
  });
  return (
    <div className="text-sm">
      <div>{laneOf({ originZoneCode, destinationZoneCode })}</div>
      <div className="text-xs text-muted">
        {jobTypeCode}
        {cargo ? ` · ${cargo}` : ''}
      </div>
    </div>
  );
}

function PriceCell({ row }: { row: RateLineImportRowView }) {
  if (!row.amount) return <span className="text-muted">—</span>;
  return (
    <div className="text-sm">
      <div>{formatMoney(row.amount)}</div>
      <div className="text-xs text-muted">
        {row.validFrom} to {row.validTo ?? 'open-ended'}
      </div>
    </div>
  );
}

function CurrentCell({ row }: { row: RateLineImportRowView }) {
  if (!row.currentLine) return <span className="text-muted">—</span>;
  return (
    <div className="text-sm">
      <div>{formatMoney(row.currentLine.amount)}</div>
      <div className="text-xs text-muted">
        {row.currentLine.validFrom} to {row.currentLine.validTo ?? 'open-ended'}
      </div>
    </div>
  );
}

function ErrorsCell({ row }: { row: RateLineImportRowView }) {
  if (!row.errors?.length) return null;
  return (
    <ul className="text-xs text-error">
      {row.errors.map((error, index) => (
        <li key={index}>{error.message}</li>
      ))}
    </ul>
  );
}

const COLUMNS: TableColumnsType<RateLineImportRowView> = [
  { title: 'Row', dataIndex: 'rowNumber', key: 'rowNumber', width: 64 },
  { title: 'Status', key: 'status', render: (_, row) => <Tag color={ROW_STATUS_COLORS[row.status]}>{ROW_STATUS_LABELS[row.status]}</Tag> },
  { title: 'Key', key: 'key', render: (_, row) => <KeyCell row={row} /> },
  { title: 'Current', key: 'current', render: (_, row) => <CurrentCell row={row} /> },
  { title: 'Proposed', key: 'proposed', render: (_, row) => <PriceCell row={row} /> },
  { title: 'Errors', key: 'errors', render: (_, row) => <ErrorsCell row={row} /> },
];

/** Every row from the sheet, in order — new, changed and unchanged beside what's in force now, invalid with every column that's wrong on it. */
export function RateLineImportRowsTable({ rows }: { rows: RateLineImportRowView[] }) {
  return <Table rowKey="rowNumber" dataSource={rows} columns={COLUMNS} pagination={false} scroll={{ x: 'max-content' }} size="small" />;
}
